import AuthenticatedAPIClient from '../lib/AuthenticatedAPIClient';
import trainTime from '../lib/TrainTime';
import { resolve, resolveAll } from '../helpers/LinkMapper';

import Favourite from '../records/Favourite';
import OrderFulfilment from '../records/order/Fulfilment';
import { FavouritesLimitExceededError } from '../errors/Favourite';
import {
    enrichFavouritesResponse,
    enrichFavouriteDeliveryResponse,
    getPlusBus
} from '../helpers/Favourites';

const FavouritesService = {

    /**
     * @param {OrderFare} fare
     * @param {OrderTrip} orderTrip
     * @param {OrderFulfilment} delivery
     * @param {User} user
     * @return {Favourite}
     */
    async addFavourite (fare, orderTrip, delivery, user) {
        try {
            const favourite = Favourite.fromFare(fare).toJSON();
            const deliveryOption = delivery.hasFulfilmentMethod() ? delivery.getPruned() : null;
            const plusBus = orderTrip ? getPlusBus(orderTrip) : {};
            const photocardNumber = fare.hasPhotocardNumber() ? fare.getPhotocardNumber() : null;

            const response = await AuthenticatedAPIClient.post(`/favourite-ticket/customer/${user.id}/one-click`, {
                ...favourite,
                ...plusBus,
                photocard: photocardNumber,
                delivery: deliveryOption
            });

            return resolve(await enrichFavouritesResponse(response, user)).as(Favourite);
        } catch (err) {
            const { response } = err;
            if (response && response.status === 422) {
                throw new FavouritesLimitExceededError();
            }
            throw err;
        }
    },

    /**
     * @param {Favourite} favourite
     * @param {User} user
     */
    async removeFavourite (favourite, user) {
        await AuthenticatedAPIClient.delete(`/favourite-ticket/customer/${user.id}/one-click/${favourite.id}`);
    },

    /**
     * @param {User} user
     * @return {Favourite[]}
     */
    async getFavourites (user) {
        const response = await AuthenticatedAPIClient.get(`/favourite-ticket/customer/${user.id}/one-click`, {}, { loader: false });

        const favourites = await Promise.all(
            response.result.map(favourite => enrichFavouritesResponse(favourite, user))
        );

        return resolveAll(favourites).as(Favourite);
    },

    /**
     * @param {Favourite} favourite
     * @param {String} photocardNumber
     * @param {User} user
     * @return {Favourite}
     */
    async updateFavouritePhotocard (favourite, photocardNumber, user) {
        const favouriteId = favourite.id;

        const response = await AuthenticatedAPIClient.patch(`/favourite-ticket/customer/${user.id}/one-click/${favouriteId}/photocard`, {
            photocard: photocardNumber
        });

        return resolve(await enrichFavouritesResponse(response, user)).as(Favourite);
    },

    /**
     * @param {Favourite} favourite
     * @param {OrderFulfilment} delivery
     * @param {User} user
     * @return {OrderFulfilment}
     */
    async addFavouriteDelivery (favourite, delivery, user) {
        const favouriteId = favourite.id;
        const deliveryOption = delivery.getPruned();

        const response = await AuthenticatedAPIClient.post(`/favourite-ticket/customer/${user.id}/one-click/${favouriteId}/delivery`, deliveryOption);
        const newDelivery = await enrichFavouriteDeliveryResponse(response, user);

        const { id } = response;
        const fulfilment = resolve(newDelivery).as(OrderFulfilment);

        return {
            id,
            fulfilment
        };
    },

    /**
     * @param {Favourite} favourite
     * @param {OrderFulfilment} delivery
     * @param {User} user
     * @return {OrderFulfilment}
     */
    async updateFavouriteDelivery (favourite, delivery, user) {
        const favouriteId = favourite.id;
        const deliveryId = favourite.deliveryId;
        const deliveryOption = delivery.getPruned();

        const response = await AuthenticatedAPIClient.put(`/favourite-ticket/customer/${user.id}/one-click/${favouriteId}/delivery/${deliveryId}`, deliveryOption);
        const updatedDelivery = await enrichFavouriteDeliveryResponse(response, user);

        return resolve(updatedDelivery).as(OrderFulfilment);
    },

    /**
     * @param {Favourite} favourite
     * @param {trainTime} seasonExpiryDate
     * @param {User} user
     * @return {Favourite}
     */
    async updateFavouriteSeasonExpiryDate (favourite, seasonExpiryDate, user) {
        const response = await AuthenticatedAPIClient.patch(`/favourite-ticket/customer/${user.id}/one-click/${favourite.id}/season-expiry-date`, {
            date: trainTime.isoDate(seasonExpiryDate)
        });

        return resolve(await enrichFavouritesResponse(response, user)).as(Favourite);
    },

    /**
     * @param {Favourite} favourite
     * @param {trainTime} lastUsedDate
     * @param {User} user
     * @return {Favourite}
     */
    async updateFavouriteLastUsedDate (favourite, lastUsedDate, user) {
        const response = await AuthenticatedAPIClient.patch(`/favourite-ticket/customer/${user.id}/one-click/${favourite.id}/used-date`, {
            date: trainTime.isoDate(lastUsedDate)
        });

        return resolve(await enrichFavouritesResponse(response, user)).as(Favourite);
    },
};

export default FavouritesService;



// WEBPACK FOOTER //
// ./src/services/FavouritesService.js